<template>
  <div class="tw-bg-white tw-rounded-xl tw-p-8 tw-max-w-2xl tw-mx-auto">
    <!-- Header -->
    <div class="tw-mb-8">
      <p class="tw-text-sm tw-text-gray-600">
        {{ t('common.actions.choose_export_format_and_select_fields') }}
      </p>
    </div>

    <!-- Export Format Section -->
    <div class="tw-mb-8">
      <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-3">
        {{ t('common.actions.download_as') }}
      </label>
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button variant="secondary" class="tw-w-full tw-justify-between tw-h-10">
            <span class="tw-truncate">
              {{ getExportFormatLabel(exportFormat) }}
            </span>
            <ChevronDown class="tw-ml-2 tw-h-4 tw-w-4 tw-opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          class="tw-w-full tw-min-w-[200px] tw-z-[200] !tw-border-none !tw-shadow-[0px_0px_19px_0px_#f5f5f5]"
          align="start"
        >
          <DropdownMenuItem
            v-for="option in exportOptions"
            :key="option.value"
            @click="exportFormat = option.value"
            :disabled="option.disabled"
            class="tw-cursor-pointer"
          >
            <div class="tw-flex tw-items-center tw-gap-2">
              <span class="tw-font-medium">{{ option.label }}</span>
              <Badge v-if="option.disabled" variant="secondary" class="tw-text-xs">
                {{ t('common.titles.disabled') }}
              </Badge>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>

    <!-- Fields Selection Section -->
    <div class="tw-mb-8">
      <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-3">
        {{ t('orders.titles.fields_to_export') }}
      </label>

      <!-- Custom Multi-Select -->
      <div class="tw-relative">
        <Popover v-model:open="isFieldsOpen">
          <PopoverTrigger as-child>
            <Button
              variant="secondary"
              role="combobox"
              :aria-expanded="isFieldsOpen"
              class="tw-w-full tw-justify-between tw-h-10"
            >
              <span class="tw-truncate">
                {{
                  selectedFields.length > 0
                    ? `${selectedFields.length} ${t('orders.titles.fields_selected')}`
                    : t('orders.titles.fields_to_export')
                }}
              </span>
              <ChevronDown class="tw-ml-2 tw-h-4 tw-w-4 tw-opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            class="tw-w-full tw-p-0 tw-z-[200] !tw-border-none !tw-shadow-[0px_0px_19px_0px_#f5f5f5]"
            align="start"
          >
            <div class="tw-p-4 tw-border-b tw-border-gray-200">
              <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
                <span class="tw-text-sm tw-font-medium tw-text-gray-700 tw-mr-2">
                  {{ t('orders.titles.fields_to_export') }}
                </span>
                <div class="tw-flex tw-gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    @click="selectAllFields"
                    class="tw-h-7 tw-px-2 tw-text-xs tw-text-blue-600 tw-hover:text-blue-800 tw-hover:bg-blue-50 tw-transition-colors"
                  >
                    {{ t('common.actions.select_all') }}
                  </Button>
                  <Button
                    v-if="selectedFields.length > 0"
                    variant="ghost"
                    size="sm"
                    @click="clearAllFields"
                    class="tw-h-7 tw-px-2 tw-text-xs tw-text-red-600 tw-hover:text-red-800 tw-hover:bg-red-50 tw-transition-colors"
                  >
                    {{ t('common.actions.clear_all') }}
                  </Button>
                </div>
              </div>
              <Input
                v-model="searchTerm"
                :placeholder="t('common.data.search')"
                class="tw-w-full"
              />
            </div>
            <div class="tw-max-h-60 tw-overflow-y-auto">
              <div class="tw-p-2">
                <label
                  v-for="field in filteredFields"
                  :key="(field as any).value"
                  class="tw-flex tw-items-center tw-space-x-2 tw-px-2 tw-py-2 tw-text-sm hover:tw-bg-gray-100 tw-cursor-pointer tw-rounded-md tw-transition-colors"
                >
                  <FancyCheckbox
                    :checked="isFieldSelected(field as any)"
                    @update:modelValue="() => toggleField(field as any)"
                    class="tw-rounded"
                  />
                  <span class="tw-flex-1">{{ (field as any).label }}</span>
                </label>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <!-- Selected Fields Display -->
      <div v-if="selectedFields.length > 0" class="tw-mt-4">
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
          <span class="tw-text-sm tw-font-medium tw-text-gray-700">
            {{ t('common.actions.selected_fields') }} ({{ selectedFields.length }})
          </span>
        </div>
        <div
          class="tw-flex tw-flex-wrap tw-gap-2 tw-max-h-32 tw-overflow-y-auto tw-p-3 tw-bg-gray-50 tw-rounded-lg tw-border"
        >
          <Badge
            v-for="(field, index) in selectedFields"
            :key="index"
            variant="secondary"
            class="tw-inline-flex tw-items-center tw-gap-1 tw-cursor-default tw-group"
          >
            {{ (field as any).label }}
            <button
              @click="removeField(index)"
              class="tw-ml-1 tw-text-secondary-600 tw-hover:text-secondary-800 tw-transition-colors tw-focus:outline-none tw-opacity-0 group-hover:tw-opacity-100"
              :title="'Remove ' + (field as any).label"
            >
              <X class="tw-w-3 tw-h-3" />
            </button>
          </Badge>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else
        class="tw-mt-4 tw-text-center tw-py-8 tw-text-gray-500 tw-bg-gray-50 tw-rounded-lg tw-border-2 tw-border-dashed tw-border-gray-200"
      >
        <FileText class="tw-w-12 tw-h-12 tw-mx-auto tw-mb-3 tw-text-gray-300" />
        <p class="tw-text-sm">{{ t('common.actions.no_fields_selected') }}</p>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="tw-flex tw-justify-end tw-gap-3 tw-pt-6 tw-border-t tw-border-gray-200">
      <Button
        variant="outline"
        @click="$emit('cancel')"
        class="tw-px-6 tw-text-secondary-500 tw-border-solid tw-border-secondary-500 hover:tw-bg-secondary-500 hover:tw-text-white"
      >
        {{ t('common.actions.cancel') }}
      </Button>
      <Button
        @click="onExport"
        :disabled="selectedFields.length === 0"
        class="tw-px-6 tw-bg-secondary-500 tw-text-white hover:tw-bg-secondary-600"
      >
        <FileExportIcon class="tw-w-4 tw-h-4 tw-mr-2" />
        {{ t('common.actions.export') }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import FancyCheckbox from '@/components/shared/fancyTables/FancyCheckbox.vue';
import { ChevronDown, X, FileText } from 'lucide-vue-next';
import FileExportIcon from '@/assets/icons/FileExport.svg';

const exportFormat = ref('xlsx');
const isFieldsOpen = ref(false);
const searchTerm = ref('');

const { t } = useI18n();

const props = defineProps({
  fields: {
    type: Array,
    required: true,
  },
  allowPdf: {
    type: Boolean,
    default: false,
  },
});

const exportOptions = ref([
  { label: 'PDF', value: 'pdf', disabled: !props.allowPdf },
  { label: 'XLSX', value: 'xlsx' },
]);

const emit = defineEmits(['export', 'cancel']);

const selectedFields: any = ref([]);

const filteredFields = computed(() => {
  if (!searchTerm.value) return props.fields;

  const filtered = props.fields.filter((field: any) =>
    field.label.toLowerCase().includes(searchTerm.value.toLowerCase())
  );

  return filtered;
});

const getExportFormatLabel = (format: string) => {
  const option = exportOptions.value.find(opt => opt.value === format);
  return option ? option.label : format.toUpperCase();
};

const isFieldSelected = (field: any) => {
  const isSelected = selectedFields.value.some((selected: any) => selected.value === field.value);
  return isSelected;
};

const toggleField = (field: any) => {
  const index = selectedFields.value.findIndex((selected: any) => selected.value === field.value);

  if (index > -1) {
    selectedFields.value.splice(index, 1);
  } else {
    selectedFields.value.push({ ...field }); // Create a copy
  }
};

const removeField = (index: number) => {
  selectedFields.value.splice(index, 1);
};

const clearAllFields = () => {
  selectedFields.value = [];
  searchTerm.value = '';
};

const selectAllFields = () => {
  // Get all filtered fields that are not already selected
  const newFields = filteredFields.value.filter(
    (field: any) => !selectedFields.value.some((selected: any) => selected.value === field.value)
  );

  // Add the new fields to the existing selection (create copies)
  selectedFields.value.push(...newFields.map((field: any) => ({ ...field })));
  searchTerm.value = '';
};

const onExport = () => {
  const data = {
    exportFormat: exportFormat.value,
    fields: selectedFields.value.map((field: any) => field.value),
  };
  emit('export', data);
};

onMounted(() => {
  // Create a deep copy to avoid reference issues
  selectedFields.value = [...props.fields];
});
</script>

<style scoped>
.field-badge {
  border-radius: 0.5rem;
  border: 1px solid #ccc;
}
.trash-icon {
  cursor: pointer;
  color: red;
  font-size: 12px;
}
</style>
